/**
 * coachMarks - sequential in-world onboarding hints that auto-dismiss as the
 * user performs each action.
 *
 * A single TOKENS card sits in the bottom-center HUD region showing the
 * current step; the input layer dispatches CustomEvents as the user acts
 * (oggi:coach:moved / looked / walked / interacted - from movement.js,
 * clickToWalk.js and interactionManager.js) and the pure sequence machine
 * (coachMarkSequence.js) advances past matching steps. The card is
 * pointerEvents:none except the Skip button, so it can never block clicks,
 * pointer lock, or Esc.
 *
 * Sequences are per control scheme, plus a single-card 'follow' variant for
 * the guided-tour onboarding choice.
 */

import { TOKENS, BRAND, createIcon, applyPanelStyle } from '../designSystem.js';
import { getHudRegion } from '../hud/hudRegions.js';
import { createSequence } from './coachMarkSequence.js';
import { X, Check } from 'lucide';

const COACH_EVENTS = [
  'oggi:coach:moved',
  'oggi:coach:looked',
  'oggi:coach:walked',
  'oggi:coach:interacted',
];

// Each step stays on screen at least this long before an action event may
// auto-advance it. Without the floor, the action the user was already doing
// (e.g. still dragging to look) dismisses the next tip before it is read.
const MIN_STEP_MS = 1500;

const CELEBRATE_STYLE_ID = 'coach-celebrate-styles';

/** One-time keyframes for the completion burst (pure CSS - no assets, CSP-safe). */
function injectCelebrationStyles() {
  if (document.getElementById(CELEBRATE_STYLE_ID)) return;
  const style = document.createElement('style');
  style.id = CELEBRATE_STYLE_ID;
  style.textContent = `
    @keyframes coach-burst {
      from { transform: translate(0, 0) scale(1); opacity: 1; }
      to { transform: translate(var(--dx), var(--dy)) scale(0.35); opacity: 0; }
    }
    .coach-burst-dot { animation: coach-burst 700ms ease-out forwards; }
  `;
  document.head.appendChild(style);
}

/** Brand-colored dots bursting from the card center on tour completion. */
function celebrate(card) {
  injectCelebrationStyles();
  const rect = card.getBoundingClientRect();
  const cx = rect.left + rect.width / 2;
  const cy = rect.top + rect.height / 2;
  const colors = [BRAND.primary, BRAND.secondary, BRAND.accent, '#22c55e'];
  const count = 14;
  for (let i = 0; i < count; i++) {
    const dot = document.createElement('span');
    const angle = (Math.PI * 2 * i) / count + (Math.random() - 0.5) * 0.5;
    const dist = 60 + Math.random() * 70;
    dot.className = 'coach-burst-dot';
    dot.style.cssText = `
      position:fixed; left:${cx}px; top:${cy}px; width:7px; height:7px;
      border-radius:50%; background:${colors[i % colors.length]};
      pointer-events:none; z-index:${TOKENS.z.toast};
      --dx:${(Math.cos(angle) * dist).toFixed(1)}px;
      --dy:${(Math.sin(angle) * dist - 40).toFixed(1)}px;
    `;
    document.body.appendChild(dot);
    setTimeout(() => dot.remove(), 800);
  }
}

const SEQUENCES = {
  classic: [
    {
      id: 'look',
      completesOn: 'oggi:coach:looked',
      text: 'Click the scene to capture your mouse, then look around (Esc frees the cursor)',
    },
    { id: 'move', completesOn: 'oggi:coach:moved', text: 'Walk with the W A S D or arrow keys' },
    {
      id: 'interact',
      completesOn: 'oggi:coach:interacted',
      text: 'Glowing labels mark the exhibits - aim the center dot at one and click to use it',
    },
  ],
  hybrid: [
    {
      id: 'look',
      completesOn: 'oggi:coach:looked',
      text: 'Hold the mouse button and drag to look around - your cursor stays free',
    },
    { id: 'move', completesOn: 'oggi:coach:moved', text: 'Walk with the W A S D or arrow keys' },
    {
      id: 'interact',
      completesOn: 'oggi:coach:interacted',
      text: 'Glowing labels mark the exhibits - walk up to one and click it',
    },
  ],
  easy: [
    {
      id: 'look',
      completesOn: 'oggi:coach:looked',
      text: 'Hold the mouse button and drag to look around',
    },
    {
      id: 'walk',
      completesOn: 'oggi:coach:walked',
      text: 'Click a spot on the floor to walk there',
    },
    {
      id: 'interact',
      completesOn: 'oggi:coach:interacted',
      text: 'Glowing labels mark the exhibits - walk up to one and click it',
    },
  ],
  touch: [
    {
      id: 'move',
      completesOn: 'oggi:coach:moved',
      text: 'Touch the lower-left of the screen and slide to walk',
    },
    { id: 'look', completesOn: 'oggi:coach:looked', text: 'Drag anywhere else to look around' },
    {
      id: 'interact',
      completesOn: 'oggi:coach:interacted',
      text: 'Glowing labels mark the exhibits - walk up to one and tap it',
    },
  ],
  follow: [
    // autoAdvanceMs: a user who genuinely sits back on the guided tour never
    // moves, so the card retires itself instead of lingering forever.
    {
      id: 'follow',
      completesOn: 'oggi:coach:moved',
      autoAdvanceMs: 12000,
      text: "You're on the guided tour - move at any time to take back control",
    },
  ],
};

/** @type {{sequence: any, card: HTMLElement, text: HTMLElement, dots: HTMLElement, handler: (e: Event) => void, offChange: () => void, clearAutoAdvance: () => void, onComplete: ((skipped: boolean) => void) | null} | null} */
let active = null;

/**
 * Start (or restart) the coach-mark sequence for a scheme/mode.
 * @param {'classic'|'easy'|'hybrid'|'touch'|'follow'|string} variant
 * @param {Object} [options]
 * @param {(skipped: boolean) => void} [options.onComplete] fired once when the
 *   sequence finishes or is skipped
 */
export function startCoachMarks(variant, { onComplete } = {}) {
  stopCoachMarks();
  // Re-arm the input layer's once-per-scene move/look latches (movement.js)
  // so a replay - or a first run where the user already moved during the
  // arrival - can still complete those steps.
  window.dispatchEvent(new CustomEvent('oggi:coach:reset'));
  const steps = SEQUENCES[variant] || SEQUENCES.classic;
  const sequence = createSequence(steps.map(({ id, completesOn }) => ({ id, completesOn })));

  // --- Card ---
  const card = document.createElement('div');
  card.id = 'coach-mark';
  applyPanelStyle(card);
  Object.assign(card.style, {
    display: 'flex',
    alignItems: 'center',
    gap: TOKENS.space.md,
    color: '#eee',
    padding: `${TOKENS.space.md} ${TOKENS.space.lg}`,
    fontFamily: TOKENS.font,
    fontSize: '14px',
    pointerEvents: 'none', // never block the scene; Skip opts back in
    maxWidth: 'min(480px, calc(100vw - 32px))',
    transition: 'opacity 0.25s ease',
  });

  const dots = document.createElement('div');
  Object.assign(dots.style, { display: 'flex', gap: '5px', flexShrink: '0' });

  const text = document.createElement('div');
  Object.assign(text.style, { lineHeight: '1.5' });

  const skip = document.createElement('button');
  skip.id = 'coach-mark-skip';
  skip.type = 'button';
  skip.title = 'Skip tips';
  skip.replaceChildren(createIcon(X, { size: 14, label: 'Skip tips' }));
  Object.assign(skip.style, {
    background: 'none',
    border: 'none',
    color: '#888',
    cursor: 'pointer',
    display: 'inline-flex',
    padding: '4px',
    pointerEvents: 'auto',
    flexShrink: '0',
  });
  skip.addEventListener('click', () => sequence.skip());

  card.append(dots, text, skip);
  getHudRegion('bottom-center').appendChild(card);

  const paintDots = () => {
    dots.replaceChildren();
    for (let i = 0; i < sequence.stepCount(); i++) {
      const dot = document.createElement('span');
      const doneStep = i < sequence.stepIndex() || sequence.isDone();
      const current = i === sequence.stepIndex() && !sequence.isDone();
      Object.assign(dot.style, {
        width: '7px',
        height: '7px',
        borderRadius: '50%',
        background: doneStep ? '#22c55e' : current ? '#8ab4ff' : 'rgba(255,255,255,0.25)',
      });
      dots.appendChild(dot);
    }
  };

  let stepShownAt = Date.now();

  const render = () => {
    const step = sequence.current();
    if (step) {
      text.textContent = steps.find((s) => s.id === step.id)?.text ?? '';
      paintDots();
      stepShownAt = Date.now();
    }
  };

  // Auto-advance: steps that declare autoAdvanceMs (the follow card) retire
  // themselves after the delay via advance() - a completion, not a skip.
  let autoTimer = null;
  const clearAutoAdvance = () => {
    if (autoTimer) {
      clearTimeout(autoTimer);
      autoTimer = null;
    }
  };
  const armAutoAdvance = () => {
    clearAutoAdvance();
    const step = sequence.current();
    const def = step ? steps.find((s) => s.id === step.id) : null;
    if (def?.autoAdvanceMs) {
      autoTimer = setTimeout(() => sequence.advance(), def.autoAdvanceMs);
    }
  };

  const finish = (skipped) => {
    // Grab the callback before any teardown clears `active`.
    const completeCallback = active ? active.onComplete : null;
    if (active) active.onComplete = null; // fire once

    // Brief "all done" beat before fading out (skips vanish immediately).
    if (!skipped) {
      paintDots();
      celebrate(card);
      text.replaceChildren(
        createIcon(Check, { size: 14 }),
        document.createTextNode(
          " You're all set - the corner map shows where everything is. Enjoy exploring!",
        ),
      );
      text.style.display = 'inline-flex';
      text.style.alignItems = 'center';
      text.style.gap = '6px';
      skip.style.display = 'none';
      setTimeout(() => {
        card.style.opacity = '0';
        // Only tear down if a replay hasn't already replaced this card.
        setTimeout(() => {
          if (active && active.card === card) stopCoachMarks();
        }, 300);
      }, 2200);
    } else {
      stopCoachMarks();
    }
    if (completeCallback) {
      try {
        completeCallback(skipped);
      } catch {
        /* ignore */
      }
    }
  };

  const offChange = sequence.onChange((_step, done) => {
    if (done) {
      clearAutoAdvance();
      finish(sequence.wasSkipped());
    } else {
      render();
      armAutoAdvance();
    }
  });

  // Buffer events that arrive before the current step has been on screen for
  // MIN_STEP_MS: the action still counts, but the tip stays readable. Safe to
  // replay late - notify() only advances when the event matches the CURRENT
  // step, so a stale buffered event is a no-op.
  const handler = (e) => {
    const remaining = MIN_STEP_MS - (Date.now() - stepShownAt);
    if (remaining > 0) {
      const type = e.type;
      setTimeout(() => {
        if (active && active.sequence === sequence) sequence.notify(type);
      }, remaining);
    } else {
      sequence.notify(e.type);
    }
  };
  COACH_EVENTS.forEach((name) => window.addEventListener(name, handler));

  active = {
    sequence,
    card,
    text,
    dots,
    handler,
    offChange,
    clearAutoAdvance,
    onComplete: onComplete || null,
  };
  render();
  armAutoAdvance();
}

/** Remove the card and listeners (safe when nothing is active). */
export function stopCoachMarks() {
  if (!active) return;
  COACH_EVENTS.forEach((name) => window.removeEventListener(name, active.handler));
  active.clearAutoAdvance();
  active.offChange();
  active.card.remove();
  active = null;
}

/** @returns {boolean} */
export function coachMarksActive() {
  return !!active;
}
